import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Button, Card, Dropdown, Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router";
import { generate } from "short-uuid";
import { z } from "zod";

import * as api from "../../api/api";
import { DockDocument } from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import BooleanFormGroup from "../../components/FormGroups/BooleanFormGroup.jsx";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";
import { numberFromString } from "../../utils/zodUtils.js";

const dockSchema = z.object({
    id: z.string(),
    name: z
        .string()
        .min(1, "Enter a name")
        .refine((value) => value.toLowerCase() !== "mobile", {
            message: "The name cannot be 'mobile'",
        }),
    position: z.object({
        x: numberFromString,
        y: numberFromString,
        z: numberFromString,
    }),
    yaw: numberFromString,
    powered: z.boolean(),
    takeoffHeight: numberFromString.positive(),
    calibrationPointOffset: z.object({
        x: numberFromString,
        y: numberFromString,
        z: numberFromString,
    }),
    returnPointOffset: z.object({
        x: numberFromString,
        y: numberFromString,
        z: numberFromString,
    }),
});

export default function DockSettingsPage() {
    const { organizationId, locationId, dockId } = useParams();
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(dockSchema),
    });
    const [isLoadingDock, setIsLoadingDock] = useState(false);
    const [droneStates] = api.useDroneStatesFromLocation(
        organizationId,
        locationId,
        { type: "map" },
    );
    const droneIds = useMemo(
        () => Object.keys(droneStates || {}),
        [droneStates],
    );

    const isNewDock = dockId === undefined;
    const backUrl = isNewDock
        ? urls.docks(organizationId, locationId)
        : urls.dock(organizationId, locationId, dockId);

    const dockPosition = watch("position");
    const dockName = watch("name");
    const takeoffHeight = watch("takeoffHeight");
    const calibrationPointOffset = watch("calibrationPointOffset");
    const calibrationPoint =
        dockPosition?.x != null &&
        dockPosition?.y != null &&
        dockPosition?.z != null &&
        calibrationPointOffset?.x != null &&
        calibrationPointOffset?.y != null &&
        calibrationPointOffset?.z != null &&
        takeoffHeight != null
            ? {
                  x: Number(dockPosition.x) + Number(calibrationPointOffset.x),
                  y: Number(dockPosition.y) + Number(calibrationPointOffset.y),
                  z:
                      Number(dockPosition.z) +
                      Number(takeoffHeight) +
                      Number(calibrationPointOffset.z),
              }
            : undefined;

    const returnPointOffset = watch("returnPointOffset");
    const returnPoint =
        dockPosition?.x != null &&
        dockPosition?.y != null &&
        dockPosition?.z != null &&
        returnPointOffset?.x != null &&
        returnPointOffset?.y != null &&
        returnPointOffset?.z != null &&
        takeoffHeight != null
            ? {
                  x: Number(dockPosition.x) + Number(returnPointOffset.x),
                  y: Number(dockPosition.y) + Number(returnPointOffset.y),
                  z:
                      Number(dockPosition.z) +
                      Number(takeoffHeight) +
                      Number(returnPointOffset.z),
              }
            : undefined;

    useEffect(() => {
        if (isNewDock) {
            setIsLoadingDock(true);
            reset({
                id: generate(),
                name: "",
                position: {
                    x: 0.0,
                    y: 0.0,
                    z: 0.0,
                },
                yaw: 90.0,
                powered: true,
                takeoffHeight: 0.7,
                calibrationPointOffset: {
                    x: 0.0,
                    y: 0.0,
                    z: 0.0,
                },
                returnPointOffset: {
                    x: 0.0,
                    y: 0.0,
                    z: 0.0,
                },
            });
            setIsLoadingDock(false);
        } else {
            setIsLoadingDock(true);
            DockDocument(organizationId, locationId, dockId)
                .get()
                .then((dock) => {
                    // Set default values for undefined settings
                    reset(dock);
                    if (dock.powered === undefined) {
                        setValue("powered", true, { shouldDirty: true });
                    }
                    if (dock.takeoffHeight === undefined) {
                        setValue("takeoffHeight", 0.7, { shouldDirty: true });
                    }
                    if (dock.calibrationPointOffset === undefined) {
                        setValue("calibrationPointOffset.x", 0.0, {
                            shouldDirty: true,
                        });
                        setValue("calibrationPointOffset.y", 0.0, {
                            shouldDirty: true,
                        });
                        setValue("calibrationPointOffset.z", 0.0, {
                            shouldDirty: true,
                        });
                    }
                    if (dock.returnPointOffset === undefined) {
                        setValue("returnPointOffset.x", 0.0, {
                            shouldDirty: true,
                        });
                        setValue("returnPointOffset.y", 0.0, {
                            shouldDirty: true,
                        });
                        setValue("returnPointOffset.z", 0.0, {
                            shouldDirty: true,
                        });
                    }
                    setIsLoadingDock(false);
                });
        }
    }, [organizationId, locationId, dockId, isNewDock, reset, setValue]);

    function copyPositionAndYawFromDrone(droneId) {
        const x = droneStates[droneId]?.widgets?.position?.data?.x;
        const y = droneStates[droneId]?.widgets?.position?.data?.y;
        const z = droneStates[droneId]?.widgets?.position?.data?.z;
        const yaw = droneStates[droneId]?.widgets?.orientation?.data?.yaw;
        if (
            x !== undefined &&
            y !== undefined &&
            z !== undefined &&
            yaw !== undefined
        ) {
            // Round values
            const roundedX = Math.round(x * 100) / 100; // 2 decimal places
            const roundedY = Math.round(y * 100) / 100; // 2 decimal places
            const roundedZ = Math.round(z * 100) / 100; // 2 decimal places
            const roundedYaw = Math.round(yaw * 10) / 10; // 1 decimal place

            setValue("position.x", roundedX, { shouldDirty: true });
            setValue("position.y", roundedY, { shouldDirty: true });
            setValue("position.z", roundedZ, { shouldDirty: true });
            setValue("yaw", roundedYaw, { shouldDirty: true });
        }
    }

    const onSubmit = async (dock) => {
        const docRef = DockDocument(organizationId, locationId, dock.id);

        if (isNewDock) {
            await docRef.set(dock); // Create new document
        } else {
            await docRef.update(dock); // Update existing document
        }

        navigate(urls.dock(organizationId, locationId, dock.id));
    };

    return (
        <Layout>
            <title>Dock Settings | MyCorvus</title>
            {isNewDock ? (
                <Breadcrumbs path={breadcrumbPaths.dockCreate} />
            ) : (
                <Breadcrumbs
                    path={breadcrumbPaths.dockSettings}
                    dock={{ name: dockName }}
                />
            )}
            <Card>
                <Title
                    backButtonUrl={backUrl}
                    title={isNewDock ? "Create dock" : "Edit dock"}
                />
                <Card.Body className="p-4 py-0">
                    {isLoadingDock ? (
                        <Spinner
                            animation="border"
                            role="status"
                            variant="primary"
                        >
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                    ) : (
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <TextFormGroup
                                label="Name"
                                name="name"
                                placeholder="Enter a name..."
                                control={control}
                                errors={errors}
                            />
                            <BooleanFormGroup
                                label="Powered (only rpv3)"
                                name="powered"
                                control={control}
                                errors={errors}
                                trueLabel="Yes"
                                falseLabel="No"
                            />
                            <br />
                            <h5>Position</h5>
                            <TextFormGroup
                                label="X"
                                name="position.x"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Y"
                                name="position.y"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Z"
                                name="position.z"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Yaw"
                                name="yaw"
                                control={control}
                                errors={errors}
                                unitText="°"
                            />
                            {droneIds.length > 0 && (
                                <Dropdown
                                    className="mt-3"
                                    onSelect={(droneId) =>
                                        copyPositionAndYawFromDrone(droneId)
                                    }
                                >
                                    <Dropdown.Toggle
                                        variant="outline-primary"
                                        id="copy-position-from"
                                    >
                                        Copy position from drone
                                    </Dropdown.Toggle>

                                    <Dropdown.Menu>
                                        {droneIds.map((droneId) => (
                                            <Dropdown.Item
                                                eventKey={droneId}
                                                key={droneId}
                                            >
                                                {droneId}
                                            </Dropdown.Item>
                                        ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                            <br />
                            <h6>Takeoff and landing</h6>
                            <TextFormGroup
                                label="Takeoff height (only rpv3)"
                                name="takeoffHeight"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <h6>
                                Calibration point offset relative to takeoff
                                position (only rpv3)
                            </h6>
                            <TextFormGroup
                                label="Offset X"
                                name="calibrationPointOffset.x"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Offset Y"
                                name="calibrationPointOffset.y"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Offset Z"
                                name="calibrationPointOffset.z"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />

                            <div>
                                Calibration point (only rpv3):
                                <code className="ms-1">
                                    {calibrationPoint
                                        ? `x: ${calibrationPoint.x.toFixed(2)}, y: ${calibrationPoint.y.toFixed(2)}, z: ${calibrationPoint.z.toFixed(2)}`
                                        : `Fill in form to see calibration point`}
                                </code>
                            </div>
                            <br />
                            <h6>
                                Return point offset relative to takeoff position
                                (rpv3.0.5+)
                            </h6>
                            <TextFormGroup
                                label="Offset X"
                                name="returnPointOffset.x"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Offset Y"
                                name="returnPointOffset.y"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />
                            <TextFormGroup
                                label="Offset Z"
                                name="returnPointOffset.z"
                                control={control}
                                errors={errors}
                                unitText="m"
                            />

                            <div>
                                Return point (rpv3.0.5+):
                                <code className="ms-1">
                                    {returnPoint
                                        ? `x: ${returnPoint.x.toFixed(2)}, y: ${returnPoint.y.toFixed(2)}, z: ${returnPoint.z.toFixed(2)}`
                                        : `Fill in form to see return point`}
                                </code>
                            </div>
                            <Button
                                variant="success"
                                disabled={isSubmitting || !isDirty}
                                type="submit"
                                className="mb-3 mt-3"
                            >
                                <FaSave className="mb-1 me-1" />
                                {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                            <CancelButton
                                className="ms-2 mb-3 mt-3"
                                to={backUrl}
                            />
                        </Form>
                    )}
                </Card.Body>
            </Card>
        </Layout>
    );
}
